import React, { useState, useEffect, useRef } from 'react';
import './LocationsLightbox.scss'
import Lightbox from "yet-another-react-lightbox-lite";
import "yet-another-react-lightbox-lite/styles.css";

function GalleryLightbox() {
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(-1);
  const filterRef = useRef(null);
  // Use a ref to store the first element of each category for efficient lookup
  const categoryStartElements = useRef({});

  useEffect(() => {
    fetch('https://beta.haromstudio.hu/scriptedprod/wp-json/wp/v2/pages/466?_embed&acf_format=standard')
      .then((response) => response.json())
      .then((data) => {
        const images = data.acf.kepek.map((item) => ({
          src: item.kep?.url,
          title: item.kep?.title || "",
          layout: item.layout || "",
          category: item.category || "", // Ensure category is correctly mapped
        }));
        setSlides(images);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  }, []);

  useEffect(() => {
    const locations = document.querySelectorAll('.grid--locations .location');
    // Populate categoryStartElements ref after locations are rendered
    categoryStartElements.current = {}; // Clear previous entries
    locations.forEach(location => {
        const categoryId = location.id;
        // Only store the first element found for each category ID
        if (categoryId && !categoryStartElements.current[categoryId]) {
            categoryStartElements.current[categoryId] = location;
        }
    });

    const updateActiveFilter = () => {
      const filterLinks = document.querySelectorAll('.grid--filter a');
      const filterHeight = filterRef.current ? filterRef.current.offsetHeight : 0;
      // Use the filter height as the threshold for determining the active section
      // Add a small buffer to account for potential sub-pixel rendering or variations
      const scrollThreshold = filterHeight + 5; // Added a small buffer

      let activeCategory = null;

      // Get the ordered list of unique category IDs from the rendered locations
      const orderedCategoryIds = [];
      locations.forEach(location => {
          if (location.id && !orderedCategoryIds.includes(location.id)) {
              orderedCategoryIds.push(location.id);
          }
      });

      // Find the first category whose start element is at or above the scroll threshold
      for (const categoryId of orderedCategoryIds) {
          const location = categoryStartElements.current[categoryId]; // Get the first element of this category
          if (location) { // Ensure the element exists
              const rect = location.getBoundingClientRect();

              // If the top of this category's first element is at or above the threshold,
              // this is the active category.
              if (rect.top <= scrollThreshold) {
                  activeCategory = categoryId;
                  // Continue the loop to find the LAST one that is above the threshold
                  // This ensures the active state stays until the NEXT category crosses the threshold
              } else {
                  // If the top of this category is below the threshold,
                  // then the active category must be the previous one we found
                  // whose top was at or above the threshold.
                  // We can stop checking further categories as they are even lower on the page.
                  break;
              }
          }
      }

      // Handle edge case: if at the very top of the page, the first element should be active
      // This applies if no category start element has yet reached the scroll threshold (activeCategory is still null).
      // Ensure the first element exists and has a valid ID.
      const firstLocation = locations.length > 0 ? locations[0] : null;
      if (activeCategory === null && firstLocation && firstLocation.id) {
           activeCategory = firstLocation.id;
      }

       // Handle edge case: if scrolled to the very bottom of the page, the last element should be active
       // Check if the bottom of the viewport is at or past the bottom of the document.
       // Ensure the last element exists and has a valid ID.
       const lastLocation = locations.length > 0 ? locations[locations.length - 1] : null;
       if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight && lastLocation && lastLocation.id) {
           activeCategory = lastLocation.id;
       }


      // Update the active class on filter links based on the determined active category
      filterLinks.forEach(link => {
        // Extract the category from the href (remove the leading '#')
        const linkCategory = link.getAttribute('href').substring(1);
        // Ensure the extracted link category is not empty before comparing
        if (linkCategory && activeCategory && linkCategory === activeCategory) {
          link.classList.add('active');
        } else {
          link.classList.remove('active');
        }
      });
    };

    // Add scroll event listener
    window.addEventListener('scroll', updateActiveFilter);

    // Initial check on mount and after slides are loaded
    // Use a small timeout to allow the DOM to render and layout to settle
    const initialCheckTimeout = setTimeout(updateActiveFilter, 100);


    // Clean up the event listener and timeout on component unmount
    return () => {
      window.removeEventListener('scroll', updateActiveFilter);
      clearTimeout(initialCheckTimeout);
    };
  }, [slides]); // Re-run effect if slides change

  return (
    <div>
      <div className='grid--filter' ref={filterRef}>
        {/* Ensure href values exactly match the expected location IDs */}
        <a href='#Classic Architecture'>Classic Architecture</a>
        <a href='#Streets'>Streets</a>
        <a href='#Modern'>Modern</a>
        <a href='#Unique'>Unique</a>
        <a href='#Subways'>Subways</a>
        <a href='#Airports'>Airports</a>
        <a href='#Train Stations'>Train Stations</a>
        <a href='#Stadiums'>Stadiums</a>
        <a href='#Bridges'>Bridges</a>
        <a href='#Concert Halls'>Concert Halls</a>
        <a href='#Libraries'>Libraries</a>
        <a href='#Classic Interiors'>Classic Interiors</a>
        <a href='#Restaurants and Bars'>Restaurants and Bars</a>
        <a href='#Industrial'>Industrial</a>
        <a href='#Castles'>Castles</a>
        <a href='#Villages'>Villages</a>
        <a href='#Nature'>Nature</a>
      </div>
      <div className='grid--locations'>
        {slides.map((slide, i) => (
          <div
            className={`location ${slide.layout}`}
            // Ensure id is set correctly and is a non-empty string matching filter link href
            id={slide.category && typeof slide.category === 'string' ? slide.category : ""}
            onClick={() => setIndex(i)}
            key={i}
            style={{backgroundImage: `url(${slide.src})`, cursor: "pointer"}}
          >
            {slide.title}
          </div>
        ))}
      </div>
      {index >= 0 && (
        <Lightbox
          slides={slides}
          index={index}
          setIndex={setIndex}
          onClose={() => setIndex(-1)}
        />
      )}
    </div>
  );
}

export default GalleryLightbox;
